import { Channel, EpgDataClient, QUERY_KEYS } from '@24i/nxg-sdk-photon';
import { useQueryClient, QueryClient } from 'react-query';
import { EpgCollection } from '@24i/nxg-sdk-epg/src/cache/collections/EpgCollection';
import useChannelsQuery from '@24i/nxg-sdk-smartott/src/hooks/query/epg/useChannelsQuery';
import { useEpgData } from '@24i/nxg-sdk-smartott-shared/src/context/EpgData';
import { useEffect, useRef, useState } from 'react';

type UseEpgCacheParams = {
    channels?: Channel[];
    startTime: number;
    endTime: number;
    // if true will index the data before fetching and any request to fetch them
    // again will return the cache, if false will not. Defaults to true.
    optimisticIndex?: boolean;
};

const filterIndexedChannels = (
    channels: Channel[],
    collection: EpgCollection,
    range: [startTime: number, endTime: number]
) => {
    return channels.filter((channelToFilter) => {
        const result = !collection.isLoadedInRange(channelToFilter.id, {
            start: range[0],
            end: range[1],
        });
        return result;
    });
};

const addDataIntoCollection = (
    channels: Channel[],
    collection: EpgCollection,
    indexRange: [startTime: number, endTime: number]
) => {
    channels.forEach((channel) => {
        collection?.addTreePrograms(channel.id, channel.programs ?? [], indexRange);
    });
    return collection;
};

const indexDataInTheCollection = (
    collection: EpgCollection,
    channels: Channel[],
    range: [startTime: number, endTime: number]
) => {
    channels.forEach((ch) =>
        collection.updateLoadedProgramsRangeIndex(ch.id, { start: range[0], end: range[1] })
    );
};

const fetchDataNotIndexedInTheCollection = async (
    queryClient: QueryClient,
    collection: EpgCollection = new EpgCollection(),
    client: EpgDataClient,
    channels: Channel[],
    range: [startTime: number, endTime: number],
    indexBeforeFetch: boolean
) => {
    const filteredChannels = filterIndexedChannels(channels, collection, range);
    if (indexBeforeFetch) indexDataInTheCollection(collection, channels, range);
    if (filteredChannels.length > 0) {
        const keys = filteredChannels
            .map((ch) => ch.id)
            .filter((a, b) => String(a).localeCompare(String(b)));

        return queryClient.fetchQuery({
            queryKey: [QUERY_KEYS.epg, range[0], range[1], ...keys],
            queryFn: () =>
                client.getEpgDataInRange(
                    {
                        start: range[0],
                        end: range[1],
                    },
                    filteredChannels
                ),
        });
    }
    return [];
};

export const useEpgCache = (params: UseEpgCacheParams) => {
    const { channels, startTime, endTime, optimisticIndex = true } = params;
    const { data: allChannels = [] } = useChannelsQuery();
    const client = useEpgData();
    const queryClient = useQueryClient();
    const finalChannels = channels ?? allChannels ?? [];
    const idString = finalChannels.map((ch) => ch.id).join(',');

    const previousCollectionRef = useRef<EpgCollection>(new EpgCollection());
    const [data, setData] = useState<Channel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const isLoadedRef = useRef<boolean>(false);

    useEffect(() => {
        if (!allChannels.length) return;

        const getCollection = async () => {
            try {
                setIsFetching(true);
                if (!isLoadedRef.current) {
                    setIsLoading(true);
                }
                const collectionData = await fetchDataNotIndexedInTheCollection(
                    queryClient,
                    previousCollectionRef.current,
                    client,
                    finalChannels,
                    [startTime, endTime],
                    optimisticIndex
                );
                const collection = addDataIntoCollection(
                    collectionData,
                    previousCollectionRef.current,
                    [startTime, endTime]
                );
                previousCollectionRef.current = collection;
                isLoadedRef.current = true;

                return (
                    finalChannels.map((channel) => {
                        return {
                            ...channel,
                            programs: collection.getPrograms(channel.id, [startTime, endTime]),
                        };
                    }) ?? []
                );
            } catch (e) {
                return [];
            } finally {
                setIsFetching(false);
                if (isLoadedRef.current) {
                    setIsLoading(false);
                }
            }
        };

        getCollection().then(setData);
    }, [startTime, endTime, idString]);
    return { data, allChannels, isLoading, isFetching };
};
